import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop } from "@stencil/core";

import {
  StzhBadgeType,
  StzhButtonBlurEvent,
  StzhButtonChangeEvent,
  StzhButtonClickEvent,
  StzhButtonFocusEvent,
  StzhButtonSize,
  StzhButtonVariant,
  StzhLocaleComponent,
} from "../../index";

import { hasSlot } from "../../utils/utils";

/**
 * @slot - Slot for label/text content
 * @slot icon - Slot for icon element
 */
@Component({
  tag: "stzh-button",
  styleUrl: "stzh-button.scss",
  scoped: true,
})
export class StzhButton {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Display a badge aligned to button or icons */
  @Prop() badge: string = "";

  /** Disable hyphenation for text inside button */
  @Prop() noHyphenation: boolean = false;

  /** Badge type */
  @Prop({ reflect: true }) badgeType: StzhBadgeType = "default";

  /** Badge position */
  @Prop({ reflect: true }) badgePosition: "icon" | "button" = "button";

  /** Whether badge should be displayed empty */
  @Prop({ reflect: true }) badgeEmpty: boolean = false;

  /** Whether the button is full width */
  @Prop({ reflect: true }) fullwidth: boolean = false;

  /** Whether the button is rounded */
  @Prop({ reflect: true }) rounded: boolean = false;

  /** Size variant */
  @Prop({ reflect: true }) size: StzhButtonSize = "default";

  /** Size variant (above small breakpoint) */
  @Prop({ reflect: true }) sizeSmall: StzhButtonSize;

  /** Size variant (above medium breakpoint) */
  @Prop({ reflect: true }) sizeMedium: StzhButtonSize;

  /** Size variant (above large breakpoint) */
  @Prop({ reflect: true }) sizeLarge: StzhButtonSize;

  /** Size variant (above ultra breakpoint) */
  @Prop({ reflect: true }) sizeUltra: StzhButtonSize;

  /** Text alignment */
  @Prop({ reflect: true }) textAlign: "left" | "center" | "right" | "space-between" | "default" = "default";

  /** Variant style */
  @Prop({ reflect: true }) variant: StzhButtonVariant = "default";

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Icon Position */
  @Prop({ reflect: true }) iconPosition: "left" | "right" = "left";

  /** Checked status (if type is radio) */
  @Prop({ mutable: true, reflect: true }) checked: boolean = false;

  /** Default checked (used by reset, if type is radio) */
  @Prop({ mutable: true }) defaultChecked: boolean;

  /** The name of the input element (if type is button or radio) */
  @Prop({ reflect: true }) name: string = "";

  /** The value of the input element (if type is button or radio) */
  @Prop({ mutable: true }) value: string = "";

  /** `href` if the button should be used as link  */
  @Prop() href: string = "";

  /** Rel (if href is used) */
  @Prop() rel: string;

  /** Target if the button is used as link (if `href` used)  */
  @Prop() target: string = "";

  /** Download attribute of link (if `href` used) */
  @Prop() download: string;

  /** Type of the button */
  @Prop() type: "button" | "submit" | "reset" | "radio" = "button";

  /** Whether the button is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether to disable the button on click or not */
  @Prop() disableOnClick: boolean = false;

  /** Whether the button is active */
  @Prop({ reflect: true }) active: boolean = false;

  /** Label */
  @Prop() label: string = "";

  /** Effect/Animation used */
  @Prop() effect: "default" | "cta" = "default";

  /** Whether only an icon is used inside the button */
  @Prop({ reflect: true }) iconOnly: boolean = false;

  /** Whether padding left should be removed */
  @Prop({ reflect: true }) noPaddingLeft: boolean = false;

  /** Whether padding right should be removed */
  @Prop({ reflect: true }) noPaddingRight: boolean = false;

  /** Access key of button (usually a number e.g. 1) */
  @Prop() buttonAccesskey: string;

  /** ID of button element */
  @Prop() buttonId: string;

  /** Whether to show toggle icon (show minus when a11yExpanded=true, otherwise plus) */
  @Prop({ reflect: true }) showToggleIcon: boolean = false;

  /** Accessible label for screen readers to replace visible text */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string;

  /** Id for element which describes the button (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /** Aria expanded of link/button */
  @Prop({ reflect: true, attribute: "a11y-expanded" }) a11yExpanded: boolean;

  /** Aria disabled of link/button */
  @Prop({ reflect: true, attribute: "a11y-disabled" }) a11yDisabled: boolean;

  /** Aria controls of link/button */
  @Prop({ attribute: "a11y-controls" }) a11yControls: string;

  /** Aria current of link/button */
  @Prop({ attribute: "a11y-current" }) a11yCurrent: string;

  /** Tabindex of link/button */
  @Prop({ attribute: "a11y-tabindex" }) a11yTabindex: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link/button element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhButtonElement;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhButtonFocusEvent>;

  /** Blur event */
  @Event() stzhBlur: EventEmitter<StzhButtonBlurEvent>;

  /** Change event (only called if type is radio) */
  @Event() stzhChange: EventEmitter<StzhButtonChangeEvent>;

  /** Click event */
  @Event() stzhClick: EventEmitter<StzhButtonClickEvent>;

  private button: HTMLButtonElement | HTMLAnchorElement | HTMLLabelElement;
  private input: HTMLInputElement;
  private text: HTMLDivElement;
  private focusedByInput: boolean = false;

  private handleReset = async () => {
    this.checked = this.defaultChecked;
  };

  private onInput = (event: InputEvent) => {
    this.checked = this.input.checked;
    this.stzhChange.emit({
      component: "stzh-button",
      originalEvent: event,
      value: this.value,
      checked: this.checked,
    });
  };

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.button.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-button",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-button",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    if (this.disabled) {
      event.stopPropagation();
      event.preventDefault();
    } else {
      if (this.disableOnClick) {
        this.disabled = true;
      }
      this.stzhClick.emit({
        component: "stzh-button",
        originalEvent: event,
        href: this.href,
      });
    }
  };

  private renderIcon(iconUsed: boolean): HTMLInputElement {
    return (
      <div class="stzh-button__icon-wrapper">
        {this.icon && <stzh-icon class="stzh-button__icon" name={this.icon}></stzh-icon>}
        {!this.icon && this.showToggleIcon && (
          <div class="stzh-button__toggle-icon-wrapper">
            <div class="stzh-button__toggle-icon"></div>
          </div>
        )}
        {!this.icon && !this.showToggleIcon && <slot name="icon"></slot>}
        {(this.badge || this.badgeEmpty) && this.badgePosition === "icon" && iconUsed && (
          <stzh-badge class="stzh-button__badge" label={this.badge} type={this.badgeType}></stzh-badge>
        )}
      </div>
    );
  }

  private renderContent(): HTMLDivElement {
    return (
      <div class="stzh-button__text" ref={el => (this.text = el as HTMLDivElement)}>
        {this.rel && this.rel.includes("external") && (
          <div class="stzh-button__vhidden">{this.localization.$globals.externalLinkLabel}</div>
        )}
        {this.download && <div class="stzh-button__vhidden">{this.localization.$globals.downloadLinkLabel}</div>}
        {this.label ? this.label : <slot></slot>}
      </div>
    );
  }

  private renderInner(iconUsed: boolean): DocumentFragment {
    return (
      <Fragment>
        <div class="stzh-button__inner">
          {this.type === "radio" && (
            <div class="stzh-button__mark">
              <div class="stzh-button__check"></div>
            </div>
          )}
          {this.iconPosition === "left" && this.renderIcon(iconUsed)}
          {this.renderContent()}
          {this.iconPosition === "right" && this.renderIcon(iconUsed)}
        </div>
        {(this.badge || this.badgeEmpty) && (this.badgePosition === "button" || !iconUsed) && (
          <stzh-badge class="stzh-button__badge" label={this.badge} type={this.badgeType}></stzh-badge>
        )}
      </Fragment>
    );
  }

  async componentWillLoad() {
    this.defaultChecked = typeof this.defaultChecked === "boolean" ? this.defaultChecked : this.checked;

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "button");
    }
  }

  componentDidRender() {
    requestAnimationFrame(() => {
      this.button?.setAttribute("s-object-id", this.analyticsId || this.text.innerText);
    });
  }

  render() {
    const iconUsed: boolean = hasSlot(this.element, "icon") || !!this.icon;
    const classes = {
      "stzh-button": true,
      "stzh-button--has-icon": iconUsed,
      "stzh-button--has-icon-only": this.iconOnly,
      "stzh-button--has-badge": !!this.badge,
      "stzh-button--is-rounded": this.rounded,
      "stzh-button--is-fullwidth": this.fullwidth,
      "stzh-button--is-disabled": this.disabled || this.a11yDisabled,
      "stzh-button--is-active": this.active,
      "stzh-button--is-expanded": this.a11yExpanded,
      "stzh-button--not-hyphenated": this.noHyphenation,
      [`stzh-button--effect-${this.effect}`]: !!this.effect,
      [`stzh-button--badge-position-${this.badgePosition}`]: !!this.badgePosition,
      [`stzh-button--align-${this.textAlign}`]: !!this.textAlign,
      [`stzh-button--type-${this.type}`]: !!this.type,
      [`stzh-button--${this.variant}`]: !!this.variant,
    };

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        {this.href ? (
          <a
            ref={el => (this.button = el as HTMLAnchorElement)}
            href={this.disabled ? null : this.href}
            rel={this.rel}
            download={this.download}
            target={this.target}
            class={classes}
            aria-label={this.a11yLabel || null}
            aria-describedby={this.a11yDescribedby || null}
            aria-disabled={typeof this.a11yDisabled !== "undefined" ? (this.a11yDisabled ? "true" : "false") : null}
            aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
            aria-controls={this.a11yControls || null}
            aria-current={this.a11yCurrent || null}
            id={this.buttonId}
            accessKey={this.buttonAccesskey}
            tabindex={this.a11yTabindex}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
          >
            {this.renderInner(iconUsed)}
          </a>
        ) : this.type === "radio" ? (
          <label ref={el => (this.button = el as HTMLLabelElement)} class={classes} onClick={this.onClick}>
            <input
              ref={el => (this.input = el as HTMLInputElement)}
              type="radio"
              class="stzh-button__input"
              name={this.name}
              value={this.value}
              defaultChecked={this.defaultChecked}
              checked={this.checked}
              disabled={this.disabled}
              aria-label={this.a11yLabel || null}
              aria-describedby={this.a11yDescribedby || null}
              aria-disabled={typeof this.a11yDisabled !== "undefined" ? (this.a11yDisabled ? "true" : "false") : null}
              aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
              aria-controls={this.a11yControls || null}
              aria-current={this.a11yCurrent || null}
              id={this.buttonId}
              accessKey={this.buttonAccesskey}
              tabindex={this.a11yTabindex}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onClick={this.onClick}
              onInput={this.onInput}
            />
            {this.renderInner(iconUsed)}
          </label>
        ) : (
          <button
            ref={el => (this.button = el as HTMLButtonElement)}
            class={classes}
            type={this.type}
            name={this.name}
            value={this.value}
            disabled={this.disabled}
            aria-label={this.a11yLabel || null}
            aria-describedby={this.a11yDescribedby || null}
            aria-disabled={typeof this.a11yDisabled !== "undefined" ? (this.a11yDisabled ? "true" : "false") : null}
            aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
            aria-controls={this.a11yControls || null}
            aria-current={this.a11yCurrent || null}
            id={this.buttonId}
            accessKey={this.buttonAccesskey}
            tabindex={this.a11yTabindex}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
          >
            {this.renderInner(iconUsed)}
          </button>
        )}
      </Host>
    );
  }
}
